import { useNavigation } from '@react-navigation/native';
import * as Sentry from '@sentry/react-native';
import { requestRecordingPermissionsAsync } from 'expo-audio';
import debounce from 'lodash.debounce';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dimensions,
  FlatList,
  Keyboard,
  Platform,
  ScrollView,
  StyleProp,
  StyleSheet,
  TextInput,
  useWindowDimensions,
  ViewStyle,
  InteractionManager,
} from 'react-native';
import { useMentions } from 'react-native-controlled-mentions';
import uuid from 'react-native-uuid';

import { Alert } from '@components/Alert';
import ChatImages from '@components/Chat/ChatImages';
import { ChatInputOptionsBar } from '@components/Chat/ChatInputOptionsBar';
import { CheckInCard } from '@components/Chat/CheckInCard';
import DocumentCard from '@components/Chat/DocumentCard';
import MentionSuggestions from '@components/Chat/MentionSuggestions';
import { ReplyMessage } from '@components/Chat/ReplyMessage';
import TagsCollection from '@components/Chat/TagsCollection';
import { VoiceCard } from '@components/Chat/VoiceCard';
import { VoiceRecord } from '@components/Chat/VoiceRecord';
import { Box, Text } from '@components/Restyle/index';
import Icon from '@components/shared/Icon/Icon';
import TooltipPopup, {
  getTooltipAnchorSpacing,
  getTooltipPointerSize,
} from '@components/Tutorialization/TooltipPopup';
import {
  useUpdateHashtagTooltipSeenMutation,
  Chat,
  DraftMessage,
  GetDraftMessageDocument,
  GetDraftMessageQuery,
  GetMeDocument,
  Document,
  LocalFile,
  Message,
  MessageCheckin,
  Project,
  Task,
  useUpdateDraftMessageMutation,
  useUpdateOnboardingStateMutation,
} from '@graphql/generated';
import { useApolloClient } from '@hooks/useApolloClient';
import useChatInput from '@hooks/useChatInput';
import { useChatTutorial } from '@hooks/useChatTutorial';
import { useChatUploadManager } from '@hooks/useChatUploadManager';
import { useDraftTextPersistence } from '@hooks/useDraftTextPersistence';
import useEmoji from '@hooks/useEmoji';
import { useGetMessageFromCache } from '@hooks/useGetMessageFromCache';
import { useLocationPermission } from '@hooks/useLocationPermission';
import useMe from '@hooks/useMe';
import { useOnboardingActive } from '@hooks/useOnboardingActive';
import { useRateAppPrompt } from '@hooks/useRateAppPrompt';
import useSendMessage, { SendDraftMessageOptions } from '@hooks/useSendMessage';
import useTagInput from '@hooks/useTagInput';
import {
  chatInputSchema,
  MAX_DOC_COUNT,
  MAX_IMAGE_COUNT,
} from '@schemas/chatInputSchema';
import { useNetInfo } from '@src/context/NetInfoContext';
import theme from '@themes/theme';
import { getMessageFromCache } from '@utils/cache/getMessageFromCache';
import { useFilePicker } from '@utils/filePicker';
import { mapLocalFileToDraftDocument } from '@utils/fileUtils';
import { insertAtSelection, TextSelection } from '@utils/insertAtSelection';
import { logError } from '@utils/logNonFatalError';
import { triggersConfig } from '@utils/mentions';
import { pixelSizeHorizontal } from '@utils/normalise';
import { isSupportUser } from '@utils/startConversationTutorial';
import {
  tagsCollectionToMessageProjects,
  tagsCollectionToMessageTasks,
} from '@utils/tagCollectionService';
import { canUploadFileToTeam } from '@utils/teams';
import {
  hasTaggableAttachment,
  shouldTriggerUploadOnboarding,
} from '@utils/uploadTagOnboarding';
import {
  compressVideos,
  getVideoUnsupportedAlertMessage,
  isVideoLike,
} from '@utils/videoCompression';

const CHAT_INPUT_TEST_ID = 'chat-message-input';
// "Tag It" pointer inset from the tooltip's left edge (TooltipPopup's chat default).
const TAG_IT_POINTER_LEFT = pixelSizeHorizontal(20);

type MyChatFormProps = {
  isGroupChat?: boolean;
  chatInputHeight: number;
  chatId?: Chat['id'];
  chatData?: any;
  toggleTagModalSheet: (value?: boolean) => void;
  onTagChange: (value: string) => void;
  onFormSubmit?: (
    body?: string,
    localFiles?: LocalFile[],
    checkin?: MessageCheckin
  ) => void;
  disableInput?: boolean;
  showLeaveMessage?: boolean | string;
  showKeyboardFocus?: boolean;
  chatInputFocusTrigger?: number;
  isFromMyChatBottomSheet?: boolean;
  isOpenCamera?: boolean;
  isSelectImage?: boolean;
  isCheckIn?: boolean;
  isPendingRequest?: boolean;
  onRegisterOpenAttachments?: (fn: () => void) => void;
  /**
   * Registers an imperative "prefill the composer" handler with the parent.
   * Used by the "Get a site photo" onboarding nudge: tapping its CTA drops the
   * request text into the input and focuses it so the user can send it.
   */
  onRegisterPrefill?: (fn: (text: string) => void) => void;
  /**
   * Fired when the user types into the chat input. May fire per keystroke —
   * handlers must be idempotent (mirrors ChatForm's onFirstActivity on web).
   */
  onFirstActivity?: () => void;
  /**
   * "Tag this message" onboarding nudge active — highlight the # button and
   * disable Send to force the user to tag before sending (mirrors web).
   */
  highlightTagButton?: boolean;
  /** Fired when the highlighted # button is tapped (parent clears the nudge). */
  onHighlightTagPress?: () => void;
};

/**
 * TA-9295: options for `sendDraftMessage` from this composer.
 *
 * Clearing the chat's unread badge (and recording a read receipt) is only
 * justified when the user is looking at the thread itself. MyChatBottomSheet
 * renders this same composer as a quick-send sheet over project/task
 * activity, where the target chat was never opened — its badge must survive.
 */
export const sendOptionsForComposer = (
  isFromMyChatBottomSheet?: boolean
): SendDraftMessageOptions => ({
  fromOpenChat: !isFromMyChatBottomSheet,
});

/**
 * TA-10466: `Keyboard.addListener('keyboardWillHide' | 'keyboardDidHide', ...)`
 * is global — it fires whenever ANY keyboard on screen hides, not just this
 * input's. Blurring unconditionally races a fresh focus/mention-trigger on
 * this input (e.g. some other field's keyboard hiding right after the user
 * taps @/# in chat), silently killing the mention popup. Only act when this
 * input is the one that's actually focused.
 */
export const handleChatInputKeyboardHide = (
  inputRef: { current: Pick<TextInput, 'isFocused' | 'blur'> | null },
  setKeyboardShow: (show: boolean) => void
) => {
  if (inputRef.current?.isFocused()) {
    setKeyboardShow(false);
    inputRef.current.blur();
  }
};

/**
 * TA-10466: same global-listener race as `handleChatInputKeyboardHide`, but
 * for the show event. Without the guard, a keyboard opening for a different
 * input on screen flips this composer's `keyboardShow` to true even though
 * it isn't focused, incorrectly showing its "keyboard shown" UI.
 */
export const handleChatInputKeyboardShow = (
  inputRef: { current: Pick<TextInput, 'isFocused'> | null },
  setKeyboardShow: (show: boolean) => void
) => {
  if (inputRef.current?.isFocused()) {
    setKeyboardShow(true);
  }
};

/**
 * Decides what to actually upload after a video-compression pass, and which
 * client IDs need their "compressing" upload state and composer attachment
 * cleared. Covers two outcomes:
 *
 * - `compressVideos` resolved: `unsupported` is whatever it excluded
 *   (TA-10871/TA-10895, Copilot review PR #5914 round 2).
 * - `compressVideos` itself threw (batch-level failure, e.g. filesystem
 *   error): none of this batch's videos got individually validated, so
 *   uploading them raw would reintroduce the exact truncated/corrupt-video
 *   bug this PR fixes. Every video in the batch is treated as unsupported;
 *   non-video files are unaffected (Copilot review, PR #5914, round 4).
 */
export const resolveVideoUploadOutcome = (
  toUpload: LocalFile[],
  videoClientIds: string[],
  outcome:
    | { ok: true; files: LocalFile[]; unsupported: LocalFile[] }
    | { ok: false }
): { filesToUpload: LocalFile[]; unsupportedClientIds: string[] } => {
  if (outcome.ok) {
    return {
      filesToUpload: outcome.files,
      unsupportedClientIds: outcome.unsupported.map((f) => f.clientId),
    };
  }
  const failedVideoIds = new Set(videoClientIds);
  return {
    filesToUpload: toUpload.filter((f) => !failedVideoIds.has(f.clientId)),
    unsupportedClientIds: videoClientIds,
  };
};

export const MyChatForm: React.FC<MyChatFormProps> = memo(
  ({
    isGroupChat,
    chatInputHeight,
    chatId,
    chatData,
    toggleTagModalSheet,
    onTagChange,
    onFormSubmit,
    disableInput,
    showLeaveMessage,
    showKeyboardFocus,
    chatInputFocusTrigger,
    isFromMyChatBottomSheet,
    isOpenCamera,
    isSelectImage,
    isCheckIn,
    isPendingRequest = false,
    onRegisterOpenAttachments,
    onRegisterPrefill,
    onFirstActivity,
    highlightTagButton,
    onHighlightTagPress,
  }) => {
    const navigation = useNavigation();
    const { launchImageSelection, launchFileSelection } = useFilePicker();
    const { ensureLocationPermission, showLocationPermissionDeniedAlert } =
      useLocationPermission();
    const { t } = useTranslation();
    const dimensions = useWindowDimensions();
    const [scrollEnabled, setScrollEnabled] = useState<boolean>(false);
    const [keyboardShow, setKeyboardShow] = useState(false);
    const [showRecording, setShowRecording] = useState(false);
    const [body, setBody] = useState<string>('');
    // TA-10152: mirror the draft text to AsyncStorage so it survives a full
    // app close/kill — the Apollo cache draft only reaches disk on the
    // AppState 'background' persist trigger, which a kill can skip entirely.
    // cacheRestoredBodyRef tracks what the mount-time Apollo cache draft
    // restore put into `body`, so onRestore can tell "body still holds the
    // (possibly stale) cache draft" apart from "the user typed since mount".
    // The AsyncStorage copy is written on every keystroke while the cache
    // draft only reaches disk on backgrounding, so after a kill the storage
    // copy is the fresher of the two and must win over the cache-restored
    // text — but never over text the user actually typed.
    const cacheRestoredBodyRef = useRef<string>('');
    const { draftTextHydrated, persistDraftText, clearPersistedDraftText } =
      useDraftTextPersistence({
        chatId,
        enabled: !isFromMyChatBottomSheet,
        onRestore: (saved) =>
          setBody((prev) =>
            !prev || prev === cacheRestoredBodyRef.current ? saved : prev
          ),
      });
    const [localFiles, setLocalFiles] = useState<LocalFile[]>([]);
    const { selectedReplyMessage, setSelectedReplyMessage } = useChatInput();
    const [replyMessage, setReplyMessage] = useState<Message | undefined>();
    const { client } = useApolloClient();
    const { me } = useMe();
    const onboardingActive = useOnboardingActive();
    const [showTagTooltip, setShowTagTooltip] = useState(false);
    const [updateHashtagTooltipSeen] = useUpdateHashtagTooltipSeenMutation();
    const { recordTrigger: recordRateAppTrigger } = useRateAppPrompt();
    const tagButtonRef = useRef<any>(null);
    const [tagButtonLayout, setTagButtonLayout] = useState<{
      x: number;
      width: number;
      height: number;
    } | null>(null);
    // Show tag tooltip only when user hasn't seen it; hide when they have
    useEffect(() => {
      if (me) {
        setShowTagTooltip(me.hasSeenTagTooltipNative === false);
      }
    }, [me]);
    const { sendDraftMessage } = useSendMessage();
    const { tags, setTags, tagsLength } = useTagInput();
    const chatInputRef = useRef<TextInput>(null);
    const selectionRef = useRef<TextSelection | null>(null);
    const [inputRemountKey, setInputRemountKey] = useState(0);
    const tagTaskLength = chatId ? JSON.stringify(tags(chatId)) : [];
    const PLACEHOLDER = t('models:chat.typeMessageHint');
    const [disableUpload, setDisableUpload] = useState(false);
    const [isExpired, setIsExpired] = useState(false);

    const { setFromValue } = useEmoji();

    const [checkin, setCheckin] = useState<MessageCheckin | undefined>();
    const { isConnected } = useNetInfo();

    const {
      shouldShowTutorialHashTag,
      setDoNotShowTutorial,
      setShowChatOptionsBar,
      myChatLocalFiles,
      myChatCheckin,
      editMedia,
      setEditMedia,
      uploadTagOnboardingStep,
      setUploadTagOnboardingStep,
    } = useChatInput();

    const {
      directUploadFiles,
      uploadingFiles,
      removeUploadingFile,
      updateCompressionProgress,
      setUploadingFiles,
    } = useChatUploadManager();
    const prevSuccessClientIdsRef = useRef<Set<string>>(new Set());

    const {
      isVisibleForChat,
      chatTutorialState,
      setChatTutorialStateAndInStorage,
    } = useChatTutorial();

    const [showOverlay, setShowOverlay] = useState<boolean>(false);
    const [showOverlayForTag, setShowOverlayForTag] = useState<boolean>(false);
    const [showOverlayForSend, setShowOverlayForSend] =
      useState<boolean>(false);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const prevShowKeyboardFocusRef = useRef(false);

    useEffect(() => {
      let isUploadingImage: boolean = false;
      uploadingFiles.map((f) => {
        if (f.status === 'uploading') {
          isUploadingImage = true;
        }
      });
      if (!isUploadingImage && isUploading) {
        setIsUploading(false);
        isVisibleForChat &&
          chatTutorialState === '3' &&
          setChatTutorialStateAndInStorage('4');
      }
    }, [uploadingFiles, isUploading]);

    useEffect(() => {
      if (isVisibleForChat && chatTutorialState === '2' && !disableInput) {
        setShowOverlay(true);
        return;
      }
      setShowOverlay(false);
    }, [isVisibleForChat, chatTutorialState, disableInput]);

    useEffect(() => {
      if (isVisibleForChat && chatTutorialState === '4' && !disableInput) {
        setShowOverlayForTag(true);
        return;
      }
      setShowOverlayForTag(false);
    }, [isVisibleForChat, chatTutorialState, disableInput]);

    useEffect(() => {
      if (isVisibleForChat && chatTutorialState === '7' && !disableInput) {
        setShowOverlayForSend(true);
        return;
      }
      setShowOverlayForSend(false);
    }, [isVisibleForChat, chatTutorialState, disableInput]);

    useEffect(() => {
      const successful = uploadingFiles.filter((f) => f.status === 'success');
      const newSuccessSet = new Set(successful.map((f) => f.file.clientId));

      let hasNewSuccess = false;
      for (const id of newSuccessSet) {
        if (!prevSuccessClientIdsRef.current.has(id)) {
          hasNewSuccess = true;
          break;
        }
      }

      if (!hasNewSuccess) return;

      prevSuccessClientIdsRef.current = newSuccessSet;

      setLocalFiles((prev) => {
        const prevMap = new Map(prev.map((f) => [f.clientId, f]));

        successful.forEach(({ file }) => {
          if (prevMap.has(file.clientId)) {
            const existing = prevMap.get(file.clientId)!;
            prevMap.set(file.clientId, {
              ...existing,
              duration: file.duration ?? existing.duration ?? null,
              size: file.size ?? existing.size ?? null,
              blobId: file.blobId ?? existing.blobId ?? '',
            });
          }
        });

        return Array.from(prevMap.values());
      });
    }, [uploadingFiles]);

    useEffect(() => {
      if (selectedReplyMessage) {
        const cacheIdentifier = client?.cache.identify(selectedReplyMessage);
        setReplyMessage({ cacheIdentifier, id: selectedReplyMessage.id });
      }
    }, [selectedReplyMessage]);

    const bodyLastChar = useMemo(() => body.charAt(body.length - 1), [body]);
    const indexBodyFirstChar = useMemo(() => body.lastIndexOf('#'), [body]);

    const tagsCollection = useMemo(() => {
      if (!chatId) return [];

      if (tagsLength > 0 && (bodyLastChar == '#' || indexBodyFirstChar >= 0)) {
        if (body.length <= 1) {
          chatInputRef.current?.clear();
          setBody('');
        } else if (indexBodyFirstChar >= 0 && body.length > 0) {
          setBody((prev) => prev.slice(0, indexBodyFirstChar));
        } else {
          setBody((prev) => prev.slice(0, -1));
        }
        toggleTagModalSheet(false);
      }

      return tags(chatId);
    }, [chatId, tagsLength, tagTaskLength]);

    const isValid = useMemo(() => {
      return chatInputSchema.isValidSync({
        body: body.trimEnd(),
        tagsCollection,
        localFiles,
        checkin,
      });
    }, [body, tagsCollection, localFiles, checkin]);

    const { getChat: chat } = chatData || { getChat: undefined };

    const getDraftMessage = () => {
      const { getDraftMessage: draftMessage } =
        client?.cache?.readQuery<GetDraftMessageQuery>({
          query: GetDraftMessageDocument,
          variables: {
            chatId,
          },
        }) || { getDraftMessage: {} as DraftMessage };

      return draftMessage;
    };

    const openEmojiModal = () => {
      setFromValue('devicechat-input');
      navigation.navigate('emoji-modal', {
        onCallback: (emoji: string) => {
          appendEmoji(emoji);
        },
      });
    };

    // Insert at the caret rather than appending (TA-11126).
    const appendEmoji = (emoji: string) => {
      // Selection offsets index the displayed plain text, not the raw body
      // (mentions are stored as markup), so splice the plain text and let the
      // mentions lib map it back to the raw value.
      const next = insertAtSelection(
        mentionState.plainText,
        emoji,
        selectionRef.current
      );
      selectionRef.current = { start: next.caret, end: next.caret };
      textInputProps.onChangeText(next.text);
    };

    const DEBOUNCE = 250;

    const debouncedOnTagChange = useCallback(
      debounce((value: string) => {
        onTagChange?.(value);
      }, DEBOUNCE),
      []
    );

    const debouncedToggleTagModal = useCallback(
      debounce((value: boolean) => {
        toggleTagModalSheet(value);
      }, DEBOUNCE),
      []
    );

    const setMessage = (text: string) => {
      if (text.length > 0) {
        onFirstActivity?.();
      }
      const textLastChar = text.charAt(text.length - 1);
      if (body.includes('#') && !text.includes('#')) {
        toggleTagModalSheet(false);
        onTagChange?.('');
        debouncedToggleTagModal(false);
      }
      if (bodyLastChar != '#' && textLastChar == '#') {
        onTagChange?.('');
        debouncedOnTagChange('');
        debouncedToggleTagModal(true);
      }
      if (text.includes('#') && textLastChar != '#') {
        const tagParts = text.split('#');
        const tag = tagParts[tagParts.length - 1];
        debouncedOnTagChange(tag);
      }

      setBody(text);
    };

    const replyMessageFromCache = useGetMessageFromCache(
      replyMessage?.cacheIdentifier
    );

    const setFiles = (newLocalFiles: LocalFile[]) => {
      if (images.length + newLocalFiles.length > MAX_IMAGE_COUNT) {
        Alert.alert(t('models:chat.input.maxSelection'));
        return;
      }

      setLocalFiles((prev) => {
        const prevMap = new Map(prev.map((f) => [f.clientId, f]));

        newLocalFiles.forEach((f) => {
          prevMap.set(f.clientId, {
            ...f,
            duration: f.duration ?? null,
            size: f.size ?? null,
            blobId: f.blobId ?? '',
          });
        });

        return Array.from(prevMap.values());
      });

      const toUpload = newLocalFiles.filter((f) => !f.blobId || f.blobId == '');

      if (toUpload.length > 0) {
        InteractionManager.runAfterInteractions(() => {
          handleUploadAndUpdate(toUpload);
        });
      } else if (
        isVisibleForChat &&
        (chatTutorialState === '2' || chatTutorialState === '3')
      ) {
        setIsUploading(true);
      }
    };

    const handleUploadAndUpdate = async (toUpload: LocalFile[]) => {
      // isVideoLike falls back to contentType -- isVideo alone can be wrong
      // for files from some pickers (Copilot review, PR #5914, round 4).
      const hasVideos = toUpload.some((f) => isVideoLike(f));
      let filesToUpload = toUpload;

      if (hasVideos) {
        const videoClientIds = toUpload
          .filter((f) => isVideoLike(f))
          .map((f) => f.clientId);

        setUploadingFiles((prev) => {
          const map = new Map(prev.map((f) => [f.file.clientId, f]));
          videoClientIds.forEach((cid) => {
            const file = toUpload.find((f) => f.clientId === cid);
            if (file) {
              map.set(cid, { file, status: 'compressing', progress: 0 });
            }
          });
          return Array.from(map.values());
        });

        // TA-10871/Copilot review PR #5914 round 2: a video unsupported in
        // every form (compressed and original) is excluded from upload --
        // clear its "compressing" upload state and its composer attachment
        // rather than leaving both stuck. TA-10932 adds the user-facing
        // explanation below. See resolveVideoUploadOutcome for the
        // batch-throw case (round 4).
        let outcome: Parameters<typeof resolveVideoUploadOutcome>[2];
        try {
          const { files, unsupported } = await compressVideos(
            toUpload,
            (fileIndex, _totalFiles, progress) => {
              const cid = videoClientIds[fileIndex];
              if (cid) {
                updateCompressionProgress(cid, progress);
              }
            }
          );
          outcome = { ok: true, files, unsupported };
        } catch (compressionErr) {
          // AdrianKao review, PR #5914: this used to say "falling back to
          // original", but resolveVideoUploadOutcome's ok:false branch
          // rejects every video in the batch as unsupported instead --
          // never uploads an unvalidated original. Message was describing
          // the opposite of what actually happens.
          logError({
            message:
              'Video compression step threw, rejecting all videos in batch',
            errorMessage:
              compressionErr instanceof Error
                ? compressionErr.message
                : String(compressionErr),
            operationName: 'compressVideos',
          });
          outcome = { ok: false };
        }

        const { filesToUpload: resolvedFiles, unsupportedClientIds } =
          resolveVideoUploadOutcome(toUpload, videoClientIds, outcome);
        filesToUpload = resolvedFiles;

        if (unsupportedClientIds.length > 0) {
          const unsupportedIds = new Set(unsupportedClientIds);
          // Awaited (Copilot review, PR #5914, round 7): removeUploadingFile
          // is async (hashes the file, may await a delete mutation, then
          // clears upload state) -- firing it without awaiting let
          // directUploadFiles below start uploading filesToUpload while a
          // rejected entry's "compressing" state was still being cleared.
          // Per-file .catch() (round 8): a rejected video's url/path can be
          // an unreadable content:// URI if selectFiles.ts's cache copy
          // failed. computeFileHash's fallback path (fileUtils.ts) has no
          // try/catch around FileSystem.getInfoAsync, so hashing an
          // unsupported URI can reject -- without this, that would reject
          // the whole Promise.all and skip directUploadFiles for every
          // *other*, valid file in this batch too.
          await Promise.all(
            toUpload
              .filter((f) => unsupportedIds.has(f.clientId))
              .map((f) =>
                removeUploadingFile(f).catch((e) => {
                  logError({
                    message:
                      'Failed to clear upload state for a rejected video',
                    errorMessage: e instanceof Error ? e.message : String(e),
                    operationName: 'removeUploadingFile',
                  });
                })
              )
          );
          setLocalFiles((prev) =>
            prev.filter((f) => !unsupportedIds.has(f.clientId))
          );
          // TA-10932: tell the user why their video(s) silently disappeared
          // from the composer, instead of leaving them to wonder.
          const alertMessage = getVideoUnsupportedAlertMessage(
            unsupportedClientIds.length,
            t
          );
          if (alertMessage) Alert.alert(alertMessage);
        }

        setLocalFiles((prev) => {
          const map = new Map(prev.map((f) => [f.clientId, f]));
          filesToUpload.forEach((f) => {
            if (map.has(f.clientId)) {
              map.set(f.clientId, { ...map.get(f.clientId)!, ...f });
            }
          });
          return Array.from(map.values());
        });
      }

      const uploaded = await directUploadFiles(filesToUpload);

      setLocalFiles((prev) => {
        const map = new Map(prev.map((f) => [f.clientId, f]));

        uploaded.forEach((item) => {
          if (map.has(item.clientId)) {
            const original = map.get(item.clientId)!;
            map.set(item.clientId, {
              ...original,
              blobId: item.blobId,
            });
          }
        });

        return Array.from(map.values());
      });

      if (
        isVisibleForChat &&
        (chatTutorialState === '2' || chatTutorialState === '3')
      ) {
        setIsUploading(true);
      }
    };

    const [updateDraftMessage] = useUpdateDraftMessageMutation();

    const updateDraftMessageCall = () => {
      const replyMessage = replyMessageFromCache ?? {
        message: null,
      };
      const params = {
        chatId,
        body: body,
        tagsCollection: tagsCollection,
        localFiles: localFiles,
        replyMessage: replyMessage.__typename ? replyMessage : null,
        checkin: checkin,
      };

      updateDraftMessage({
        variables: { chatId, attributes: params },
      });
    };

    useEffect(() => {
      // TA-10152: hold all draft writes until hydration resolves — this
      // effect fires on first mount with an empty body and previously wiped
      // the surviving cache draft before the restore effect could read it.
      // persistDraftText is in the deps so a chatId change re-runs this
      // effect against the new chat's writer (its identity is keyed on
      // chatId); draftTextHydrated flips false in that same render, so the
      // write is held until the new chat's storage read resolves. The write
      // itself is immediate (not debounced) — the storage copy must hold the
      // latest keystroke so a force-kill at any instant keeps the draft.
      // updateDraftMessageCall is intentionally not a dep — it is recreated
      // every render, and this effect is keyed to the draft data it writes.
      if (!draftTextHydrated) return;
      updateDraftMessageCall();
      persistDraftText(body);
    }, [
      body,
      tagsCollection,
      localFiles,
      replyMessage,
      checkin,
      draftTextHydrated,
      persistDraftText,
    ]);

    const removeLocalFile = async (file: LocalFile) => {
      const newLocalFiles = localFiles.filter((i) => i.id !== file.id);

      setLocalFiles(newLocalFiles);
      removeUploadingFile(file);
    };

    const removeTagsCollection = (indexToDelete: number) => {
      const newTagsCollection = tagsCollection?.filter(
        (_i, index: number) => index !== indexToDelete
      );

      chatId && setTags(chatId, newTagsCollection);
    };

    const removeTagsCollectionTask = (
      tagIndex: number,
      collectionIndex: number
    ) => {
      const newTagsColl = [...(tagsCollection || [])];
      const newTasksColl = newTagsColl?.[collectionIndex].tasks.filter(
        (_i, index: number) => index !== tagIndex
      );
      const updatedTagsColl = newTagsColl.map((item, index) =>
        index === collectionIndex ? { ...item, tasks: newTasksColl } : item
      );

      chatId && setTags(chatId, updatedTagsColl);
    };

    useEffect(() => {
      const hasExpiredTag = tagsCollection?.some(
        (item) =>
          (item.project?.team?.subscriptionDetails &&
            new Date(
              item.project?.team?.subscriptionDetails?.expirationDate
            ).getTime() < new Date().getTime()) ||
          item.project?.team?.subscriptionDetails?.isSubscriptionCanceled
      );
      setIsExpired(hasExpiredTag);
    }, [tagsCollection?.length]);

    const audioMessages = localFiles.filter((item) => item.isAudio);
    const images = localFiles.filter((item) => item.isImage);
    const documents = localFiles.filter(
      (item) => !item.isImage && !item.isAudio
    );

    const { textInputProps, triggers, mentionState } = useMentions({
      value: body,
      onChange: (t) => {
        setMessage(t);
      },
      onSelectionChange: (selection) => {
        selectionRef.current = selection;
      },
      triggersConfig,
    });

    const showOptionsBar =
      body ||
      keyboardShow ||
      !!localFiles.length ||
      !!tagsCollection?.length ||
      !!checkin;

    const showInputBox = !showOptionsBar;

    useEffect(() => {
      setShowChatOptionsBar(!!showOptionsBar);
    }, [showOptionsBar]);

    // First-attachment tag onboarding — Step 1/3 "Tag It" at the # button.
    // Triggers once a photo/document actually appears in the composer (not on
    // icon press) and the user has never tagged before.
    const [updateOnboardingState] = useUpdateOnboardingStateMutation();
    const uploadOnboardingFiredRef = useRef(false);

    // firstTagUsed is read only once it has actually loaded (explicit false):
    // a cached `me` without onboardingState must not block — the effect
    // re-runs via the dep below when getMe resolves after the attach.
    const firstTagUsedState = me?.onboardingState?.firstTagUsed;
    const isHelpdeskChat =
      !chat?.isGroupChat &&
      isSupportUser(chat?.users?.find((u: { id: string }) => u.id !== me?.id));

    useEffect(() => {
      if (Platform.OS === 'web') return;
      if (!onboardingActive) return;
      if (isHelpdeskChat) return;
      if (!hasTaggableAttachment(localFiles)) return;
      if (firstTagUsedState !== false) return;
      if (
        !shouldTriggerUploadOnboarding(
          firstTagUsedState,
          uploadOnboardingFiredRef.current
        )
      )
        return;
      uploadOnboardingFiredRef.current = true;
      // The legacy "Tag your message!" tooltip shares the # anchor — never
      // show both at once.
      setShowTagTooltip(false);
      setUploadTagOnboardingStep('attachTooltip');
      updateOnboardingState({
        variables: { attributes: { firstTagUsed: true } },
        refetchQueries: [{ query: GetMeDocument }],
      });
    }, [onboardingActive, localFiles, firstTagUsedState, isHelpdeskChat]);

    const handleUploadTooltipTryIt = () => {
      setUploadTagOnboardingStep('tagList');
      toggleTagModalSheet(true);
    };

    useEffect(() => {
      if (editMedia && editMedia.length > 0) {
        const selectableImagesCount = MAX_IMAGE_COUNT - images.length;

        if (selectableImagesCount > 0) {
          editMedia && setFiles(editMedia);
        } else {
          Alert.alert(t('models:chat.input.maxSelection'));
        }
        setEditMedia([]);
      }
    }, [editMedia]);

    const getCameraPhoto = (r: LocalFile[]) => {
      const selectableImagesCount = MAX_IMAGE_COUNT - images.length;

      if (selectableImagesCount > 0) {
        r && setFiles(r);
      } else {
        Alert.alert(t('models:chat.input.maxSelection'));
      }
    };

    const loadCameraComponent = () => {
      const selectableImagesCount = MAX_IMAGE_COUNT - images.length;
      if (selectableImagesCount > 0) {
        navigation.navigate(
          isFromMyChatBottomSheet
            ? 'expo-camera-photo-add-file'
            : 'expo-camera-photo',
          {
            onCallback: getCameraPhoto,
            photoCount: selectableImagesCount,
          }
        );
      } else {
        Alert.alert(t('models:chat.input.maxSelection'));
      }
    };

    const launchChatCamera = async () => {
      if (localFiles.some((item) => item.isAudio)) {
        Alert.alert(
          t('shared:alert'),
          t('models:chat.input.mediaConflictAudioImages')
        );
        return;
      }
      if (localFiles.some((item) => !item.isImage)) {
        Alert.alert(
          t('shared:alert'),
          t('models:chat.input.mediaConflictFiles')
        );
        return;
      }

      loadCameraComponent();
    };

    const pickChatImages = async () => {
      setDoNotShowTutorial(true);
      if (localFiles.some((item) => item.isAudio)) {
        Alert.alert(
          t('shared:alert'),
          t('models:chat.input.mediaConflictAudioImages')
        );
        return;
      }
      if (localFiles.some((item) => !item.isImage && !item.isAudio)) {
        Alert.alert(
          t('shared:alert'),
          t('models:chat.input.mediaConflictFiles')
        );
        return;
      }
      const selectableImagesCount = MAX_IMAGE_COUNT - images.length;

      if (selectableImagesCount > 0) {
        await launchImageSelection(selectableImagesCount, true).then((r) => {
          if (r) {
            setFiles(r);
          } else if (
            isVisibleForChat &&
            (chatTutorialState === '2' || chatTutorialState === '3')
          ) {
            openChatAttachmentsModal();
          }
        });
      } else {
        Alert.alert(t('models:chat.input.maxSelection'));
      }
    };

    const pickChatFiles = async () => {
      setDoNotShowTutorial(true);
      if (localFiles.some((item) => item.isAudio)) {
        Alert.alert(
          t('shared:alert'),
          t('models:chat.input.mediaConflictAudioFiles')
        );
        return;
      }
      if (localFiles.some((item) => item.isImage)) {
        Alert.alert(
          t('shared:alert'),
          t('models:chat.input.mediaConflictImages')
        );
        return;
      }
      const selectableDocumentsCount =
        MAX_DOC_COUNT -
        localFiles.filter((item) => !item.isImage && !item.isAudio).length;
      if (selectableDocumentsCount > 0) {
        await launchFileSelection(selectableDocumentsCount, true).then((r) => {
          if (!r) {
            if (
              isVisibleForChat &&
              (chatTutorialState === '2' || chatTutorialState === '3')
            ) {
              openChatAttachmentsModal();
            }
            return;
          }

          const documents = r.map((item) => {
            return {
              ...item,
              path: '',
              isAudio: false,
              isImage: false,
            };
          });
          setFiles(documents);
        });
      } else {
        Alert.alert(t('models:chat.input.maxSelection'));
      }
    };

    const showRecordingBox = async () => {
      if (audioMessages.length > 0) {
        Alert.alert(t('shared:alert'), t('models:chat.input.maxAudio'));
        return;
      }
      if (images.length > 0) {
        Alert.alert(
          t('shared:alert'),
          t('models:chat.input.mediaConflictImagesAudio')
        );
        return;
      }
      if (documents.length > 0) {
        Alert.alert(
          t('shared:alert'),
          t('models:chat.input.mediaConflictFilesAudio')
        );
        return;
      }
      try {
        if (Platform.OS === 'web') {
          setShowRecording(true);
        } else {
          const audioRP = await requestRecordingPermissionsAsync();
          if (audioRP.status === 'granted') {
            setShowRecording(true);
          } else {
            Alert.alert(
              t('shared:allowAudioPermission'),
              t('models:chat.audioPermission')
            );
          }
        }
      } catch (err) {
        logError({
          message: 'Audio recording permission error',
          errorMessage: err instanceof Error ? err.message : String(err),
          operationName: 'audioRecordingPermission',
        });
      }
    };
    useEffect(() => {
      const didTransitionToTrue =
        showKeyboardFocus && !prevShowKeyboardFocusRef.current;
      prevShowKeyboardFocusRef.current = Boolean(showKeyboardFocus);

      if (!didTransitionToTrue) return;

      setKeyboardShow(true);
      const interaction = InteractionManager.runAfterInteractions(() => {
        chatInputRef.current?.focus();
      });

      return () => {
        interaction.cancel();
      };
    }, [showKeyboardFocus]);

    useEffect(() => {
      if (!chatInputFocusTrigger) return;
      setKeyboardShow(true);
      const interaction = InteractionManager.runAfterInteractions(() => {
        chatInputRef.current?.focus();
      });
      return () => interaction.cancel();
    }, [chatInputFocusTrigger]);

    useEffect(() => {
      const showSubscription = Keyboard.addListener(
        Platform.OS === 'ios' ? 'keyboardWillShow' : 'keyboardDidShow',
        () => handleChatInputKeyboardShow(chatInputRef, setKeyboardShow)
      );
      const hideSubscription = Keyboard.addListener(
        Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide',
        () => handleChatInputKeyboardHide(chatInputRef, setKeyboardShow)
      );

      return () => {
        showSubscription.remove();
        hideSubscription.remove();
      };
    }, []);
    const onAssignTaskPress = () => {
      if (isFromMyChatBottomSheet) {
        navigation.navigate('chats-stack', {
          screen: 'task-modal',
          params: {
            chatId,
            isFrom: 'Chat',
          },
        });
      } else {
        navigation.navigate('task-modal', { chatId, isFrom: 'Chat' });
      }
    };

    const onCompleteTaskPress = () => {
      if (isFromMyChatBottomSheet) {
        navigation.navigate('chats-stack', {
          screen: 'task-modal',
          params: {
            chatId,
            complete: true,
            isFrom: 'Chat',
          },
        });
      } else {
        navigation.navigate('task-modal', {
          chatId,
          complete: true,
          isFrom: 'Chat',
        });
      }
    };

    const openChatAttachmentsModal = useCallback(() => {
      if (showOverlay) {
        setShowOverlay(false);
        setChatTutorialStateAndInStorage('3');
      }
      setTimeout(() => {
        navigation.navigate(
          isFromMyChatBottomSheet ? 'app-chat-attachments' : 'chat-attachments',
          {
            onCameraPress: launchChatCamera,
            onImagePress: pickChatImages,
            onDocumentPress: pickChatFiles,
            onAssignTaskPress,
            onCompleteTaskPress,
            onCheckinPress: loadCheckin,
          }
        );
      }, 500);
    }, [showOverlay, navigation, chatId, localFiles]);

    const openChatAttachmentsModalRef = useRef(openChatAttachmentsModal);
    openChatAttachmentsModalRef.current = openChatAttachmentsModal;

    useEffect(() => {
      onRegisterOpenAttachments?.(() => openChatAttachmentsModalRef.current());
    }, [onRegisterOpenAttachments]);

    // Drop a prefilled body into the composer and focus it (photo-request
    // nudge CTA). Mirrors the attachments registration so the parent can
    // trigger it imperatively.
    const prefillComposer = useCallback((text: string) => {
      setMessage(text);
      // TA-10921 #25: iOS doesn't re-measure a multiline TextInput whose value
      // is set programmatically while unfocused, so the prefilled message was
      // clipped to one line. Remounting it measures the new content.
      setInputRemountKey((k) => k + 1);
      setKeyboardShow(true);
      InteractionManager.runAfterInteractions(() => {
        chatInputRef.current?.focus();
      });
    }, []);
    const prefillComposerRef = useRef(prefillComposer);
    prefillComposerRef.current = prefillComposer;

    useEffect(() => {
      onRegisterPrefill?.((text: string) => prefillComposerRef.current(text));
    }, [onRegisterPrefill]);

    const loadCheckin = async () => {
      const hasPermission = await ensureLocationPermission();

      if (!hasPermission) {
        showLocationPermissionDeniedAlert();
      } else {
        setCheckin(undefined);
        navigation.navigate(
          isFromMyChatBottomSheet ? 'app-checkin-modal' : 'checkin-modal',
          {
            isFromMyChatBottomSheet,
            onCallback: (
              result: MessageCheckin,
              project?: Project,
              task?: Task
            ) => {
              setCheckin(result);
              if (project && me && chatId) {
                const existingCollection = tagsCollection?.find(
                  (t) => t.project?.id === project.id
                );
                if (!existingCollection) {
                  const newTagsColl = [
                    ...(tagsCollection || []),
                    {
                      tasks: task ? [task] : [],
                      project: { ...project },
                      author: me,
                    },
                  ];
                  setTags(chatId, newTagsColl);
                } else if (task) {
                  const existingCollectionForTask = tagsCollection?.find((t) =>
                    t.tasks?.find((item) => item.id === task.id)
                  );
                  if (!existingCollectionForTask) {
                    const collectionToUpdate = tagsCollection?.findIndex(
                      (t) => t.project?.id === task.project?.id
                    );
                    const newTagsColl = [...(tagsCollection ?? [])];
                    newTagsColl[collectionToUpdate] = {
                      ...newTagsColl[collectionToUpdate],
                      tasks: [...newTagsColl[collectionToUpdate].tasks, task],
                    };
                    setTags(chatId, newTagsColl);
                  }
                }
              }
            },
          }
        );
      }
    };

    const resetForm = () => {
      toggleTagModalSheet(false);
      // immediate delete in the send handler — runs before the next render's
      // draft-write effect, so a kill right after send cannot resurrect a
      // draft that was just sent (TA-10152)
      clearPersistedDraftText();
      setBody('');
      chatId && setTags(chatId, []);
      setLocalFiles([]);
      setReplyMessage(undefined);
      setSelectedReplyMessage(undefined);
      setCheckin(undefined);
    };

    const submitForm = async () => {
      chatInputRef.current?.clear();

      if (!me || !client || !chat) return;
      const { cacheIdentifier, id: replyId } = replyMessage || {
        cacheIdentifier: undefined,
      };
      const replyMessageFromCache = getMessageFromCache(
        client,
        cacheIdentifier
      );

      // NOTE: draftMessageId has multiple purposes:
      // - Default identity in Apollo uses ID and __typename. Assigning a UUID as a draft message ID is sufficiently unique.
      // - Associated objects with a draft message also need an ID. Reusing the same UUID in combination with a different __typename ensures uniqueness.
      //    For example, a draft message may have associated projects, tasks, and/or documents.
      // - Since a draft message may have multiple associated objects of the same type, we append an index value to maintain uniqueness.
      const draftMessageId = uuid.v4().toString();

      // TODO memoize?
      const messageProjects = tagsCollectionToMessageProjects(
        tagsCollection || [],
        draftMessageId,
        me
      );

      const messageTasks = tagsCollectionToMessageTasks(
        tagsCollection || [],
        draftMessageId,
        me
      );

      const documents = (localFiles || [])
        .filter((item) => !!item)
        .map((item: LocalFile) =>
          mapLocalFileToDraftDocument(item, draftMessageId, me)
        );

      const bodyWithoutHash =
        body && bodyLastChar === '#' ? body.slice(0, -1) : body;

      const params = {
        __typename: 'Message' as const,
        id: draftMessageId,
        chatId,
        clientId: draftMessageId,
        body: bodyWithoutHash,
        authorId: me?.id,
        author: me,
        createdAt: new Date().toISOString(),
        isSender: true,
        isDraft: true,
        publishedAt: false,
        publish: false,
        failedAt: !isConnected ? new Date().toISOString() : null,
        readAt: null,
        isRetry: !isConnected,
        readReceipts: null,
        chat,
        replyId,
        replyMessage: replyMessageFromCache ?? undefined,
        attachments: [...messageProjects, ...messageTasks, ...documents],
        checkin: checkin,
      };

      const shouldClear = !shouldShowTutorialHashTag;
      if (shouldClear) {
        sendDraftMessage(
          params,
          sendOptionsForComposer(isFromMyChatBottomSheet)
        );
        if (checkin) {
          // Check-in submitted — counts toward the rate-app prompt; per spec
          // the modal appears 3 seconds after the qualifying check-in
          recordRateAppTrigger('checkin', { delayMs: 3000 });
        }
        if ((tagsCollection?.length ?? 0) > 0) {
          // Message sent with tags — counts once toward the rate-app prompt
          // regardless of how many projects/tasks were tagged; per spec the
          // modal appears immediately when due
          recordRateAppTrigger('chat_tags');
        }
        resetForm();
        setDoNotShowTutorial(false);
      }
    };

    const setupBodyForTagging = () => {
      // TA-10443: hashtag button intermittently stops responding on native
      // with no error. Breadcrumb confirms whether the handler is even
      // invoked when a repro next occurs.
      Sentry.addBreadcrumb({
        category: 'chat.tagButton',
        message: 'Hashtag button pressed: setupBodyForTagging invoked',
        // Do not log `bodyLastChar`/`body` here — user-typed message content.
        data: { isLastCharHash: bodyLastChar === '#', showTagTooltip },
        level: 'info',
      });
      setDoNotShowTutorial(true);
      // If tooltip is open, close and mark as seen
      if (showTagTooltip) {
        setShowTagTooltip(false);
        updateHashtagTooltipSeen({ variables: { web: false } });
      }
      if (!body) {
        setMessage('');
      }
      if (body && bodyLastChar !== '#') {
        setBody((prev) => `${prev}`);
      }

      if (bodyLastChar == '#') {
        setBody(body.slice(0, -1));
        toggleTagModalSheet(false);
        updateDraftMessageCall();
        return;
      }
      if (showOverlayForTag) {
        setShowOverlayForTag(false);
        setChatTutorialStateAndInStorage('5');
      }
      updateDraftMessageCall();
      toggleTagModalSheet(true);
    };

    const optionsBar = (
      <Box position='relative'>
        <ChatInputOptionsBar
          onMicSelect={showRecordingBox}
          highlightTagButton={highlightTagButton}
          onHighlightTagPress={() => {
            // "Tag this message" nudge: tapping # clears the parent nudge and
            // opens the tag list (Step 2) directly — the owner skips the Step 1
            // "Tag It" tooltip. Reuses the upload-tag flow so the placed-tag
            // tooltip and success label follow as for attachments.
            onHighlightTagPress?.();
            handleUploadTooltipTryIt();
          }}
          onTagPress={
            // While the "Tag It" tooltip is up, the # button acts as its
            // "Try it!" CTA — advance to Step 2 and open the tag list.
            uploadTagOnboardingStep === 'attachTooltip'
              ? handleUploadTooltipTryIt
              : setupBodyForTagging
          }
          onImagePress={pickChatImages}
          onCameraPress={launchChatCamera}
          onDocumentPress={pickChatFiles}
          onEmojiPress={openEmojiModal}
          onSend={() => {
            setBody(body.trimEnd());
            onFormSubmit?.(body, localFiles, checkin);
            submitForm();
          }}
          onPlusSelect={openChatAttachmentsModal}
          onLocationPress={loadCheckin}
          chatId={chatId}
          disableSend={
            isFromMyChatBottomSheet
              ? tagsCollection.length === 0 ||
                !isValid ||
                disableUpload ||
                isExpired
              : !isValid || disableUpload || isExpired
          }
          isFromMyChatBottomSheet={isFromMyChatBottomSheet}
          // Attach ref to tag button for tooltip alignment
          tagButtonRef={tagButtonRef}
          onTagButtonLayout={setTagButtonLayout}
        />
      </Box>
    );

    useEffect(() => {
      if (isOpenCamera || isSelectImage) {
        myChatLocalFiles && setFiles(myChatLocalFiles);
      } else if (isCheckIn) {
        myChatCheckin && setCheckin(myChatCheckin);
      }
    }, []);

    useEffect(() => {
      if (isFromMyChatBottomSheet) return;
      const {
        body: draftBody,
        tagsCollection: draftTagsCollection,
        localFiles: draftLocalFiles,
        replyMessage: draftReplyMessage,
        checkin: draftCheckin,
      } = getDraftMessage() || {};
      if (draftBody) {
        // remember the cache-restored text so the AsyncStorage restore
        // (fresher after a kill) may replace it — but not user typing
        cacheRestoredBodyRef.current = draftBody;
        setBody(draftBody);
      }
      draftTagsCollection &&
        draftTagsCollection.length > 0 &&
        chatId &&
        setTags(chatId, draftTagsCollection);
      draftLocalFiles && setFiles(draftLocalFiles);
      draftCheckin && setCheckin(draftCheckin);
      if (draftReplyMessage) {
        const cacheIdentifier = client?.cache.identify(draftReplyMessage);
        setReplyMessage({ cacheIdentifier, id: draftReplyMessage.id });
      }
      return () => {};
    }, []);

    useEffect(() => {
      const hasExpiredTag = tagsCollection.some((item) =>
        canUploadFileToTeam(item.project?.team)
      );
      setDisableUpload(hasExpiredTag);
    }, [tagsCollection?.length]);

    if (!chatId) return null;

    const isMentionActive =
      triggers.mention.keyword !== undefined && keyboardShow;
    const dynamicStyles = isMentionActive
      ? {
          backgroundColor: theme.colors.grey06,
          height: Dimensions.get('window').height,
          justifyContent: 'flex-end',
        }
      : {
          backgroundColor: theme.colors.white,
          height: 0,
          justifyContent: 'center',
        };

    return (
      <Box
        position='relative'
        style={{
          zIndex: showOverlayForSend ? 99999 : undefined,
        }}>
        <Box
          style={[styles.suggestionBox, dynamicStyles as StyleProp<ViewStyle>]}>
          <Box>
            <MentionSuggestions
              {...triggers.mention}
              isGroupChat={isGroupChat}
              chatInputHeight={chatInputHeight}
              keyboardShow={keyboardShow}
              chatUsers={chat?.users ?? []}
            />
          </Box>
        </Box>
        {chat?.isUserBlocked && (
          <Box
            backgroundColor='white'
            borderTopStartRadius='m'
            borderTopEndRadius='m'
            borderTopWidth={1}
            borderRightWidth={1}
            borderLeftWidth={1}
            borderColor='grey02'
            zIndex={2}>
            <Text variant='labelSmall' textAlign='center' paddingVertical='m'>
              {t('models:chat.blockmessage')}
            </Text>
          </Box>
        )}
        {chatData?.getChat?.friendRequestAccepted &&
          !chat?.isUserBlocked &&
          !showLeaveMessage && (
            <Box
              backgroundColor='white'
              borderTopStartRadius='m'
              borderTopEndRadius='m'
              borderTopWidth={1}
              borderRightWidth={1}
              borderLeftWidth={1}
              borderColor='grey02'
              zIndex={2}>
              {!showRecording && (
                <>
                  <ScrollView
                    showsVerticalScrollIndicator={false}
                    keyboardShouldPersistTaps='always'
                    onContentSizeChange={(_w, h) => setScrollEnabled(h > 500)}
                    scrollEnabled={Platform.OS !== 'web' && scrollEnabled}
                    style={{
                      maxHeight: Platform.OS !== 'web' ? 500 : undefined,
                      marginBottom: theme.spacing.s,
                      ...Platform.select({
                        web: {
                          overflow: 'visible',
                          margin: theme.spacing.m,
                        },
                      }),
                    }}>
                    {replyMessage?.id && replyMessageFromCache && (
                      <Box marginTop='xs' marginHorizontal='m'>
                        <ReplyMessage
                          message={replyMessageFromCache}
                          onDismiss={() => {
                            setReplyMessage(undefined);
                            setSelectedReplyMessage(undefined);
                          }}
                        />
                      </Box>
                    )}

                    {/* ATTACHMENTS */}

                    <ScrollView
                      showsVerticalScrollIndicator={false}
                      keyboardShouldPersistTaps='always'
                      scrollEnabled
                      style={{
                        maxHeight: Platform.OS !== 'web' ? 300 : undefined,
                      }}>
                      {/* TAG CLOUD */}
                      {tagsCollection?.map((coll, index) => {
                        return (
                          <Box
                            key={coll?.project?.id}
                            marginTop={Platform.OS !== 'web' ? 'xs' : undefined}
                            marginVertical={
                              Platform.OS === 'web' ? 'xs' : undefined
                            }
                            marginHorizontal={
                              Platform.OS !== 'web' ? 'm' : undefined
                            }>
                            <TagsCollection
                              type='Chat'
                              tagsCollection={coll}
                              showAuthor={false}
                              onDeleteProject={() =>
                                tagsCollection[index].tasks.length === 0
                                  ? removeTagsCollection(index)
                                  : undefined
                              }
                              onDelete={(i: number) =>
                                removeTagsCollectionTask(i, index)
                              }
                            />
                          </Box>
                        );
                      })}
                      {/* IMAGES */}
                      {images.length > 0 && (
                        <Box
                          marginVertical={
                            Platform.OS === 'web' ? 'xs' : undefined
                          }>
                          <ChatImages
                            onDelete={removeLocalFile}
                            list={images}
                            onPress={(image: Document | LocalFile) => {
                              navigation.navigate(
                                'chat-composer-image-preview',
                                {
                                  images: images,
                                  initialIndex: images.findIndex(
                                    (img) => img.id === image.id
                                  ),
                                  onDelete: removeLocalFile,
                                }
                              );
                            }}
                          />
                        </Box>
                      )}

                      {/* VOICE RECORDINGS */}
                      {audioMessages.length > 0 && (
                        <Box mt='xs' marginHorizontal='m'>
                          <VoiceCard
                            voice={{ ...audioMessages[0] }}
                            onDelete={removeLocalFile}
                          />
                        </Box>
                      )}

                      {/* DOCUMENTS */}
                      {documents.length > 0 && (
                        <Box mt='xs'>
                          <FlatList
                            keyboardShouldPersistTaps='always'
                            data={documents}
                            horizontal={Platform.OS !== 'web'}
                            scrollEnabled={
                              Platform.OS !== 'web' && documents.length > 1
                            }
                            keyExtractor={(item) => item.id}
                            renderItem={({ item }) => (
                              <Box
                                style={{
                                  width:
                                    Platform.OS !== 'web'
                                      ? dimensions.width - 92
                                      : undefined,
                                  marginTop:
                                    Platform.OS === 'web'
                                      ? theme.spacing.xxs
                                      : undefined,
                                }}>
                                <DocumentCard
                                  document={{ ...item }}
                                  onDelete={removeLocalFile}
                                />
                              </Box>
                            )}
                            ItemSeparatorComponent={() => (
                              <Box marginRight='xxs' />
                            )}
                            ListFooterComponent={() => <Box marginRight='xs' />}
                            ListHeaderComponent={() => <Box marginLeft='m' />}
                          />
                        </Box>
                      )}

                      {/* CHECKIN */}
                      {checkin && (
                        <Box width='80%' marginLeft='m' marginTop='xs'>
                          <CheckInCard
                            checkin={checkin}
                            onClose={() => setCheckin(undefined)}
                          />
                        </Box>
                      )}
                    </ScrollView>
                    {isExpired || disableUpload ? (
                      <Box
                        flexDirection='row'
                        marginHorizontal='m'
                        marginTop='s'>
                        <Icon
                          name='Info'
                          color='alertRed'
                          height={16}
                          width={16}
                        />

                        <Text
                          marginHorizontal='xs'
                          textAlignVertical='center'
                          variant='metadata'
                          fontSize={12}
                          fontWeight='400'
                          color='alertRed'>
                          The project’s team plan has expired
                        </Text>
                      </Box>
                    ) : null}
                    <Box
                      flexDirection='row'
                      alignItems={Platform.select({
                        default: 'flex-start',
                        web: 'center',
                      })}
                      {...Platform.select({
                        default: {
                          paddingTop: 's',
                          paddingBottom: 'xs',
                          paddingHorizontal: 'xs',
                        },
                        web: {},
                      })}>
                      {showInputBox && !disableInput && (
                        <Icon
                          name='Plus'
                          variant='l'
                          color='textPrimary'
                          onPress={openChatAttachmentsModal}
                          marginTop='xxxs'
                          accessibilityLabel='Chat Options'
                        />
                      )}

                      {!disableInput && (
                        <TextInput
                          key={inputRemountKey}
                          autoCorrect={true}
                          placeholderTextColor={theme.colors.textSecondary}
                          placeholder={PLACEHOLDER}
                          multiline
                          style={[styles.input]}
                          accessibilityLabel={PLACEHOLDER}
                          testID={CHAT_INPUT_TEST_ID}
                          {...textInputProps}
                          ref={chatInputRef}
                          onFocus={(e) => {
                            setKeyboardShow(true);
                            textInputProps.onFocus?.(e);
                          }}
                          onBlur={(e) => {
                            setKeyboardShow(false);
                            textInputProps.onBlur?.(e);
                          }}
                        />
                      )}
                      {showInputBox &&
                        !disableInput &&
                        Platform.OS !== 'web' && (
                          <>
                            <Icon
                              name='Smile'
                              variant='m'
                              color='textPrimary'
                              marginRight='s'
                              marginTop='xxs'
                              onPress={openEmojiModal}
                              accessibilityLabel='Add Emoji'
                            />
                            <Icon
                              name='Mic'
                              variant='l'
                              color='textPrimary'
                              onPress={showRecordingBox}
                              marginTop='xxxs'
                            />
                          </>
                        )}
                    </Box>
                  </ScrollView>
                </>
              )}

              {showRecording && (
                <VoiceRecord
                  setShowRecording={(bool: boolean) => setShowRecording(bool)}
                  voiceFileHandler={(voice) => setFiles([voice])}
                />
              )}
              {showOptionsBar && !showRecording && (
                // A plain Box, not a non-scrolling ScrollView: ScrollView clips
                // its children (Android ignores overflow: 'visible'), which cut
                // off the pulsing # ripple. With no scrollable ancestor, taps
                // here don't dismiss the keyboard, so keyboardShouldPersistTaps
                // is not needed.
                <Box marginBottom='m'>{optionsBar}</Box>
              )}
            </Box>
          )}
        {showLeaveMessage && (
          <Box
            borderTopWidth={1}
            borderColor='grey02'
            backgroundColor='grey02'
            paddingVertical='m'
            paddingHorizontal='m'
            alignItems='center'
            justifyContent='center'>
            <Text
              variant='metadataSecondary'
              color='onSurfaceSecondary'
              fontWeight='500'
              textAlign='center'>
              {isPendingRequest
                ? t('shared:noChatMemberMessage')
                : t('shared:noLongerMessage')}
            </Text>
          </Box>
        )}
        {/* {showTagTooltip &&
          keyboardShow &&
          uploadTagOnboardingStep === 'idle' && (
            <Box
              position='absolute'
              bottom={56}
              left={56}
              zIndex={99999}
              pointerEvents='box-none'>
              <TooltipPopup
                isFromChat
                animate={true}
                visible={showTagTooltip}
                title='Tag your message!'
                description='Tap # to link this message to a project or task.'
                pointerPosition='bottom-left'
                primaryBtnTitle='Got it'
                primaryBtn={() => {
                  setShowTagTooltip(false);
                  updateHashtagTooltipSeen({ variables: { web: false } });
                }}
              />
            </Box>
          )} */}
        {uploadTagOnboardingStep === 'attachTooltip' &&
          showOptionsBar &&
          tagButtonLayout && (
            // TA-10921 #28: arrow centred on the # button, tip 4px above it.
            // The options bar is the last row of the form (above its `m`
            // bottom margin), so the # top sits that far up from our bottom.
            <Box
              position='absolute'
              bottom={
                theme.spacing.m +
                tagButtonLayout.height +
                getTooltipAnchorSpacing()
              }
              left={
                tagButtonLayout.x +
                tagButtonLayout.width / 2 -
                (TAG_IT_POINTER_LEFT + getTooltipPointerSize() / 2)
              }
              zIndex={99999}
              pointerEvents='box-none'>
              <TooltipPopup
                isFromChat
                animate={true}
                visible
                title={t('models:chat.onboarding.hashTagStep1.title')}
                description={t(
                  'models:chat.onboarding.hashTagStep1.description'
                )}
                pointerPosition='bottom-left'
                bottomLeftPos={TAG_IT_POINTER_LEFT}
                currentStep={1}
                totalSteps={3}
                onSkip={handleUploadTooltipTryIt}
                skipBtnTitle={t('models:chat.onboarding.hashTagStep1.cta')}
              />
            </Box>
          )}
        {showOverlay && (
          <Box
            position='absolute'
            bottom={72}
            left={16}
            alignSelf='flex-start'
            zIndex={99999}>
            <TooltipPopup
              isFromChat
              animate={true}
              visible={showOverlay}
              title='Connect and Explore!'
              description='Hit the ‘+’ button to discover features beyond just chatting!'
              pointerPosition='bottom-left'
              currentStep={2}
              totalSteps={7}
            />
            <Box
              style={{
                position: 'absolute',
                bottom: -62,
                left: -8,
                backgroundColor: theme.colors.white,
                zIndex: 99999,
                width: 48,
                height: 48,
                justifyContent: 'center',
                alignItems: 'center',
              }}
              borderRadius='xs'>
              <Icon
                name='Plus'
                variant='l'
                color='textPrimary'
                onPress={openChatAttachmentsModal}
                accessibilityLabel='Chat Options'
              />
            </Box>
          </Box>
        )}
        {showOverlayForTag && (
          <Box
            position='absolute'
            bottom={77}
            left={56}
            alignSelf='flex-start'
            zIndex={99999}>
            <TooltipPopup
              isFromChat
              animate={true}
              visible={showOverlayForTag}
              title='Everything’s Connected'
              description='Tag using ‘#’ to easily organize your photos/files to a project or task.'
              pointerPosition='bottom-left'
              currentStep={4}
              totalSteps={7}
            />
            <Box
              style={{
                position: 'absolute',
                bottom: -62,
                left: 5,
                backgroundColor: theme.colors.white,
                zIndex: 99999,
                width: 48,
                height: 48,
                justifyContent: 'center',
                alignItems: 'center',
              }}
              borderRadius='xs'>
              <Icon
                name='Hash'
                backgroundColor='white'
                variant='l'
                color='textPrimary'
                onPress={setupBodyForTagging}
                accessibilityLabel='Hash - Attach tags'
              />
            </Box>
          </Box>
        )}
        {showOverlayForSend && (
          <Box
            position='absolute'
            bottom={72}
            right={8}
            alignSelf='flex-start'
            zIndex={99999}>
            <TooltipPopup
              isFromChat
              animate={true}
              visible={showOverlayForSend}
              title='All Set!'
              description='You are ready to send your first tagged message!'
              pointerPosition='bottom-right'
              currentStep={7}
              totalSteps={7}
            />
            <Box
              style={{
                position: 'absolute',
                bottom: -54,
                right: 8,
                zIndex: 99999,
                justifyContent: 'center',
                alignItems: 'center',
              }}
              borderRadius='xs'>
              <Icon
                name='Send_v2'
                backgroundColor='black'
                variant='l'
                color='white'
                onPress={() => {
                  if (showOverlayForSend) {
                    setShowOverlayForSend(false);
                    setChatTutorialStateAndInStorage('8');
                  }
                  setBody(body.trimEnd());
                  onFormSubmit?.(body, localFiles, checkin);
                  submitForm();
                }}
                accessibilityLabel='Send - Send message'
              />
            </Box>
          </Box>
        )}
      </Box>
    );
  }
);
const styles = StyleSheet.create({
  input: {
    ...((Platform.OS === 'web' && {
      outlineStyle: 'none',
      maxHeight: 5 * theme.textVariants.body1.lineHeight,
    }) || {
      marginHorizontal: theme.spacing.xs,
      marginTop: -theme.spacing.xxxs,
    }),
    flex: 1,
    paddingVertical: theme.spacing.xxxs,
    textAlignVertical: 'center',
    ...theme.textVariants.body,
    color: theme.colors.textPrimary,
    fontSize: theme.textVariants.body1.fontSize,
    fontFamily: theme.textVariants.body1.fontFamily,
    lineHeight: theme.textVariants.body1.lineHeight,
    paddingBottom: -theme.spacing.m,

    maxHeight: theme.textVariants.body1.lineHeight * 6 + theme.spacing.xxs,
  },
  topShadow: {
    shadowColor: 'black',
    shadowOpacity: Platform.OS === 'android' ? 0.5 : 0.25,
    shadowOffset: { width: 0, height: 25 },
    shadowRadius: 20,
    elevation: 25,
  },
  suggestionBox: {
    position: 'absolute',
    bottom: `100%`,
    left: 0,
    right: 0,
    maxHeight: Dimensions.get('window').height < 700 ? 200 : 750, // for iPhone SE
    shadowColor: Platform.OS === 'android' ? `rgba(0,0,0,0.5)` : 'black',
    shadowOpacity: 0.05,
    shadowOffset: { width: 0, height: 5 },
    shadowRadius: 25,
    elevation: 20,
    zIndex: 3,
  },
});
